import { IconArrowUndoUp } from 'central-icons/IconArrowUndoUp';
import { IconCircleInfo } from 'central-icons/IconCircleInfo';
import { IconCrossSmall } from 'central-icons/IconCrossSmall';
import React from 'react';

import { Button, type ToastId } from '@revelio/ui';

import { useToast } from '../hooks/useToast';
import styles from './UndoToast.module.css';

interface ShowUndoToastOptions {
  message: string;
  onUndo: () => void;
  duration?: number;
}

export const useUndoToast = () => {
  const toast = useToast();
  const toastIdRef = React.useRef<ToastId | undefined>();

  const showUndoToast = ({
    message,
    onUndo,
    duration = 5000,
  }: ShowUndoToastOptions) => {
    const handleUndoClick = () => {
      onUndo();

      if (toastIdRef.current) {
        toast.update(toastIdRef.current, {
          status: 'info',
          duration: 2000,
          title: 'Page restored.',
          isClosable: true,
        });
      }
    };

    if (toastIdRef.current && toast.isActive(toastIdRef.current)) {
      toast.close(toastIdRef.current);
    }

    toastIdRef.current = toast({
      position: 'bottom',
      duration,
      render: ({ onClose }) => (
        <UndoToast
          message={message}
          onUndoClick={handleUndoClick}
          onClose={onClose}
        />
      ),
    });
  };

  return showUndoToast;
};

interface UndoToastProps {
  message: string;
  onUndoClick: () => void;
  onClose: () => void;
}

const UndoToast = ({ message, onUndoClick, onClose }: UndoToastProps) => {
  return (
    <div className={styles.toast} role="status">
      <span className={styles.icon}>
        <IconCircleInfo size="20px" />
      </span>
      <div className={styles.message}>{message}</div>
      <div className={styles.actions}>
        <Button
          size="xs"
          variant="ghost"
          color="lightBlue"
          leftIcon={<IconArrowUndoUp size="14px" />}
          onClick={onUndoClick}
        >
          Undo
        </Button>
        <button
          type="button"
          className={styles.close}
          aria-label="Close"
          onClick={onClose}
        >
          <IconCrossSmall size="14px" />
        </button>
      </div>
    </div>
  );
};
